import type { ColumnDef, RowData, Table } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { Result } from '@/shared/helpers/Result';

import { type UseDataTableProps, useDataTable } from '../../use-data-table';
import { createCellEditingStore, type CellEditingStore } from '../core/create-cell-editing-store';
import { createStagedValueStore, type StagedValueStore } from '../core/create-staged-value-store';
import type { EditableCellCommitArgs } from '../core/editable-cell-display';
import { wrapEditableColumns } from '../core/wrap-editable-columns';
import type { EditableTableSchema } from '../types';

export type CommitCellArgs<TData extends RowData> = EditableCellCommitArgs<TData>;

export type UseEditableCellDataTableProps<TData extends Record<string, unknown>> = Omit<
  UseDataTableProps<TData>,
  'columns'
> & {
  columns: ColumnDef<TData, any>[];
  schema: EditableTableSchema;
  onCommitCell: (args: CommitCellArgs<TData>) => Promise<void | Result<unknown>>;
};

export type UseEditableCellDataTableResult<TData extends RowData> = {
  mode: 'cell';
  table: Table<TData>;
  cellEditingStore: CellEditingStore;
  stagedValueStore: StagedValueStore;
};

export function useEditableCellDataTable<TData extends Record<string, unknown>>({
  columns,
  data,
  getRowId,
  onCommitCell,
  schema,
  ...props
}: UseEditableCellDataTableProps<TData>): UseEditableCellDataTableResult<TData> {
  const onCommitCellRef = useRef(onCommitCell);
  onCommitCellRef.current = onCommitCell;

  const cellEditingStore = useMemo(() => createCellEditingStore(), []);
  const stagedValueStore = useMemo(() => createStagedValueStore(), []);

  const editableGetRowId = useCallback<NonNullable<UseDataTableProps<TData>['getRowId']>>(
    (row, index, parent) => {
      const resolvedId = getRowId ? getRowId(row, index, parent) : row.id;
      if (typeof resolvedId !== 'string') {
        throw new Error('useEditableCellDataTable requires each row id to be a stable string');
      }
      return resolvedId;
    },
    [getRowId],
  );

  const editableColumns = useMemo<ColumnDef<TData, any>[]>(
    () =>
      wrapEditableColumns({
        columns,
        schema,
        mode: 'cell',
        onCommit: (args) => onCommitCellRef.current(args),
        onRequestEditCell: ({ cellKey }) => cellEditingStore.requestOpenCell(cellKey),
      }),
    [cellEditingStore, columns, schema],
  );

  const table = useDataTable({
    ...props,
    data,
    columns: editableColumns,
    getRowId: editableGetRowId,
  });

  useEffect(() => {
    const rowsById = table.getRowModel().rowsById;
    // Reconciliation currently requires canonical data to match the staged value exactly.
    // Server-normalized values will keep showing the staged value until a future edit clears it;
    // supporting those safely would require storing each cell's original base value and clearing
    // only when canonical data differs from that base, rather than on any incoming refetch.
    stagedValueStore.reconcile((rowId, columnId) => rowsById[rowId]?.getValue(columnId));
  }, [data, stagedValueStore, table]);

  return { mode: 'cell', table, cellEditingStore, stagedValueStore };
}
